import type {
  CustoEscopoPadrao,
  CustoPorPeriodicidade,
  EditarEscopoPadraoBody,
  NovoEscopoPadraoBody,
  PeriodicidadeEscopoPadrao,
  VisaoEscopoPadrao,
} from "@/modules/financeiro-configuracoes";
import {
  ESCOPO_MAX,
  MSG_CUSTO_INVALIDO,
  MSG_EMBARCACAO_OBRIGATORIA,
  MSG_EQUIPAMENTO_OBRIGATORIO,
  MSG_ESCOPO_LONGO,
  MSG_ESCOPO_OBRIGATORIO,
  MSG_GRUPO_OBRIGATORIO,
  MSG_HORAS_INVALIDAS,
  MSG_MOEDA_OBRIGATORIA,
  MSG_TIPO_OBRIGATORIO,
} from "./escopo-padrao-dialog.constants";

const VISAO_POR_CODIGO: Readonly<Record<string, VisaoEscopoPadrao>> = {
  "100": "OUTROS",
  "101": "CLASSE",
  "102": "CLASSE",
  "103": "EQUIPAMENTOS",
  "105": "OUTROS",
  "301": "OUTROS",
};

export const visaoDoTipo = (codigo: string): VisaoEscopoPadrao | null =>
  VISAO_POR_CODIGO[codigo] ?? null;

const PERIODICIDADES: Readonly<Record<VisaoEscopoPadrao, readonly PeriodicidadeEscopoPadrao[]>> = {
  EQUIPAMENTOS: ["INTERMEDIARIA", "OVERHAUL"],
  CLASSE: ["UNICA"],
  OUTROS: ["UNICA"],
};

export const periodicidadesDaVisao = (visao: VisaoEscopoPadrao) => PERIODICIDADES[visao];

export const visaoTemGrupo = (visao: VisaoEscopoPadrao | null): boolean =>
  visao === "CLASSE" || visao === "EQUIPAMENTOS";

export interface CustoForm {
  horas: number | null;
  valorServicos: number | null;
  moedaServicosId: string | null;
  valorMateriais: number | null;
  moedaMateriaisId: string | null;
}

export const CUSTO_VAZIO: CustoForm = {
  horas: null,
  valorServicos: null,
  moedaServicosId: null,
  valorMateriais: null,
  moedaMateriaisId: null,
};

export interface EscopoPadraoForm extends Record<string, unknown> {
  tipoObraId: string | null;
  embarcacaoId: string | null;
  grupoId: string | null;
  equipamentoId: string | null;
  escopo: string;
  INTERMEDIARIA: CustoForm;
  OVERHAUL: CustoForm;
  UNICA: CustoForm;
}

export const FORM_VAZIO: EscopoPadraoForm = {
  tipoObraId: null,
  embarcacaoId: null,
  grupoId: null,
  equipamentoId: null,
  escopo: "",
  INTERMEDIARIA: CUSTO_VAZIO,
  OVERHAUL: CUSTO_VAZIO,
  UNICA: CUSTO_VAZIO,
};

const temAteDuasCasas = (valor: number) => Math.abs(valor * 100 - Math.round(valor * 100)) < 1e-9;

export function erroValor(valor: number | null): string | null {
  if (valor === null) return null;
  return Number.isFinite(valor) && valor >= 0 && temAteDuasCasas(valor) ? null : MSG_CUSTO_INVALIDO;
}

export const erroMoeda = (valor: number | null, moedaId: string | null): string | null =>
  valor !== null && !moedaId ? MSG_MOEDA_OBRIGATORIA : null;

export function erroHoras(horas: number | null): string | null {
  if (horas === null) return null;
  return Number.isInteger(horas) && horas > 0 ? null : MSG_HORAS_INVALIDAS;
}

export function erroEscopo(escopo: string): string | null {
  const texto = escopo.trim();
  if (!texto) return MSG_ESCOPO_OBRIGATORIO;
  return texto.length > ESCOPO_MAX ? MSG_ESCOPO_LONGO : null;
}

export const erroTipo = (tipoObraId: string | null) => (tipoObraId ? null : MSG_TIPO_OBRIGATORIO);
export const erroEmbarcacao = (id: string | null) => (id ? null : MSG_EMBARCACAO_OBRIGATORIA);
export const erroGrupo = (id: string | null) => (id ? null : MSG_GRUPO_OBRIGATORIO);
export const erroEquipamento = (id: string | null) => (id ? null : MSG_EQUIPAMENTO_OBRIGATORIO);

export interface ErrosCusto {
  horas: string | null;
  valorServicos: string | null;
  moedaServicosId: string | null;
  valorMateriais: string | null;
  moedaMateriaisId: string | null;
}

export function errosDoCusto(custo: CustoForm, comHoras: boolean): ErrosCusto {
  return {
    horas: comHoras ? erroHoras(custo.horas) : null,
    valorServicos: erroValor(custo.valorServicos),
    moedaServicosId: erroMoeda(custo.valorServicos, custo.moedaServicosId),
    valorMateriais: erroValor(custo.valorMateriais),
    moedaMateriaisId: erroMoeda(custo.valorMateriais, custo.moedaMateriaisId),
  };
}

export const temErroNoCusto = (erros: ErrosCusto): boolean =>
  Object.values(erros).some((erro) => erro !== null);

export function errosDeIdentificacao(
  form: EscopoPadraoForm,
  visao: VisaoEscopoPadrao | null,
): Partial<Record<"tipoObraId" | "embarcacaoId" | "grupoId" | "equipamentoId" | "escopo", string>> {
  return {
    tipoObraId: erroTipo(form.tipoObraId) ?? undefined,
    embarcacaoId: erroEmbarcacao(form.embarcacaoId) ?? undefined,
    grupoId: visaoTemGrupo(visao) ? (erroGrupo(form.grupoId) ?? undefined) : undefined,
    equipamentoId:
      visao === "EQUIPAMENTOS" ? (erroEquipamento(form.equipamentoId) ?? undefined) : undefined,
    escopo:
      visao === "CLASSE" || visao === "OUTROS" ? (erroEscopo(form.escopo) ?? undefined) : undefined,
  };
}

export function algumErroDeCusto(form: EscopoPadraoForm, visao: VisaoEscopoPadrao): boolean {
  return periodicidadesDaVisao(visao).some((periodicidade) =>
    temErroNoCusto(errosDoCusto(form[periodicidade], visao === "EQUIPAMENTOS")),
  );
}

export function custosDoForm(
  form: EscopoPadraoForm,
  visao: VisaoEscopoPadrao,
): CustoPorPeriodicidade[] {
  return periodicidadesDaVisao(visao).map((periodicidade) => {
    const custo = form[periodicidade];
    return {
      periodicidade,
      horas: visao === "EQUIPAMENTOS" ? custo.horas : null,
      valorServicos: custo.valorServicos,
      moedaServicosId: custo.valorServicos === null ? null : custo.moedaServicosId,
      valorMateriais: custo.valorMateriais,
      moedaMateriaisId: custo.valorMateriais === null ? null : custo.moedaMateriaisId,
    };
  });
}

export function montarCorpoNovo(
  form: EscopoPadraoForm,
  visao: VisaoEscopoPadrao,
): NovoEscopoPadraoBody | null {
  if (!form.tipoObraId || !form.embarcacaoId) return null;
  const base = {
    tipoObraId: form.tipoObraId,
    embarcacaoId: form.embarcacaoId,
    custos: custosDoForm(form, visao),
  };
  if (visao === "EQUIPAMENTOS") {
    return {
      ...base,
      grupoEscopoMacroId: form.grupoId,
      equipamentoSfiId: form.equipamentoId,
    };
  }
  if (visao === "CLASSE") {
    return { ...base, grupoEscopoMacroId: form.grupoId, escopo: form.escopo.trim() };
  }
  return { ...base, escopo: form.escopo.trim() };
}

export const montarCorpoEdicao = (
  form: EscopoPadraoForm,
  visao: VisaoEscopoPadrao,
): EditarEscopoPadraoBody => ({ custos: custosDoForm(form, visao) });

const custoDaLinha = (custo: CustoEscopoPadrao | undefined): CustoForm =>
  custo
    ? {
        horas: custo.horas,
        valorServicos: custo.valorServicos,
        moedaServicosId: custo.moedaServicos?.id ?? null,
        valorMateriais: custo.valorMateriais,
        moedaMateriaisId: custo.moedaMateriais?.id ?? null,
      }
    : CUSTO_VAZIO;

export function formDaLinha(linha: {
  tipoObra: { id: string };
  embarcacao: { id: string } | null;
  grupo: { id: string } | null;
  equipamento: { id: string } | null;
  escopo: string;
  custos: CustoEscopoPadrao[];
}): EscopoPadraoForm {
  const custo = (periodicidade: PeriodicidadeEscopoPadrao) =>
    custoDaLinha(linha.custos.find((c) => c.periodicidade === periodicidade));
  return {
    tipoObraId: linha.tipoObra.id,
    embarcacaoId: linha.embarcacao?.id ?? null,
    grupoId: linha.grupo?.id ?? null,
    equipamentoId: linha.equipamento?.id ?? null,
    escopo: linha.escopo,
    INTERMEDIARIA: custo("INTERMEDIARIA"),
    OVERHAUL: custo("OVERHAUL"),
    UNICA: custo("UNICA"),
  };
}
